import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Users, Wrench, Clock, ArrowRight } from "lucide-react";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";

const REASONS = [
  {
    icon: Users,
    title: "One team, sketch to handover",
    body: "The same designer, project lead, and construction manager carry your build from the first conversation through to the keys.",
  },
  {
    icon: ShieldCheck,
    title: "Fixed-price guarantee",
    body: "The number on your contract is the number you pay. No surprise variations, no creep, no fine print.",
  },
  {
    icon: Wrench,
    title: "Built by our own teams",
    body: "We don't farm out the trades. Our supervisors are on every site, and our quality team signs off every stage.",
  },
  {
    icon: Clock,
    title: "Aftercare built in",
    body: "6-month and 12-month aftercare reviews are part of every build — not an upsell, not optional.",
  },
];

const STANDARDS = [
  "Full-size brickwork, not brick veneer cladding",
  "2.7m ceilings on the ground floor",
  "Square-set cornices in living areas",
  "Engineered stone benchtops as standard",
  "Energy-rated glazing and insulation across the build",
  "Independent site inspections at four key milestones",
];

export default function WhyBuildPage() {
  return (
    <>
      <SEO
        title="Why Build with Us"
        description="What makes building with Shere-e-Punjab Homes different — fixed pricing, one team, and aftercare baked in."
      />

      <PageHero
        eyebrow="Why us"
        title="Why build with Shere-e-Punjab Homes"
        description="A short list of things we don't compromise on, and why builders who promise the same can't always deliver."
      />

      <Container className="py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {REASONS.map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="rounded-2xl border border-border-subtle bg-bg-elevated p-6"
            >
              <div className="grid h-11 w-11 place-items-center rounded-md bg-accent/15 text-accent">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-xl text-text-primary">{title}</h3>
              <p className="mt-2 text-sm text-text-secondary">{body}</p>
            </article>
          ))}
        </div>

        <section className="mt-16 rounded-2xl border border-border-subtle bg-bg-elevated p-8 lg:p-12">
          <p className="eyebrow">Standard inclusions</p>
          <h2 className="mt-3 font-display text-3xl text-text-primary lg:text-4xl">
            What other builders quote as upgrades
          </h2>
          <p className="mt-3 max-w-2xl text-text-secondary">
            These are baseline on every Shere-e-Punjab Homes build, not premium options layered on
            top of a stripped-back base price.
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
            {STANDARDS.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-text-secondary">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border-subtle bg-bg-elevated p-8">
          <div>
            <h3 className="font-display text-2xl text-text-primary">Want to talk through a build?</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Drop in to one of our display homes — no booking required.
            </p>
          </div>
          <Link to="/request-an-appointment" className="btn-primary">
            Request an appointment
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>
    </>
  );
}
